import React from 'react';
import PropTypes from 'prop-types';
import ArticleDetail, { DynamicShareButtonContainer, TagChip }
from '@fuww/library/src/Article';
import Anchor from '@fuww/library/src/Anchor';
import SocialShare from '@fuww/library/src/SocialShare';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import messages from '../../../lib/messages';
import FormattedDate from '../../FormattedDate';
import { Link } from '../../../routes';
import shareList from '../../../lib/shareData';
import getArticlesField from '../../../lib/queries/news/getArticlesField';
import { sponsorIds } from '../../../lib/constants';
import { useSiteContext } from '../../SiteContext';
import Image from '../../Image';
import getCategoryTitle from '../../../lib/getCategoryTitle';
import getAuthorSlug from '../../../lib/getAuthorSlug';
import LinkAndAnchor from '../../LinkAndAnchor';
import sizes from './sizes';
import loading from '../../../lib/loading';

const DynamicNewsletterComponent = dynamic(() => import('../../NewsletterForm'),
  {
    ssr: false,
    loading,
  });

const AuthorPageLink = ({ linkText, authorEmail }) => (
  authorEmail
  && (
    <LinkAndAnchor
      route="author"
      params={{ authorSlug: getAuthorSlug(authorEmail) }}
    >
      {linkText}
    </LinkAndAnchor>
  )
);

AuthorPageLink.propTypes = {
  linkText: PropTypes.string.isRequired,
  authorEmail: PropTypes.string,
};

AuthorPageLink.defaultProps = {
  authorEmail: null,
};

const Main = ({
  article, ArticleComponent, srcSet,
}) => {
  const {
    body, category, title, createdAt,
    imageUrls: originalImageUrls, tags, labels, reference,
    imageCaption,
  } = article;
  const { origin, url } = useSiteContext();
  const [metaImageUrl, ...imageUrls] = originalImageUrls;
  const creator = { ...article.creator };

  const { prefix, hasLocalNews, locale } = useSiteContext();
  const intl = useIntl();
  const filteredShareList = shareList({
    title,
    media: `${origin}${metaImageUrl}`,
    url,
    locale,
  }).filter((share) => !share.checkMedia || imageUrls[0]);

  const articlesField = getArticlesField(prefix, hasLocalNews);
  const isSponsored = sponsorIds[articlesField] === creator.id;
  const { name: author, email } = creator;

  return (
    <ArticleComponent
      title={title}
      category={category || { title: '' }}
      translatedCategoryTitle={getCategoryTitle(category, hasLocalNews, intl)}
      articleBy={(
        <AuthorPageLink
          linkText={intl.formatMessage(messages.articleBy, { author })}
          authorEmail={email}
        />
      )}
      createdAt={<FormattedDate value={createdAt} />}
      body={body}
      image={imageUrls[0]
        && (
          <Image
            placeholder={imageUrls[0]}
            src={imageUrls[1]}
            srcSet={srcSet}
            alt={title}
            sizes={sizes}
            finalFetchpriority="high"
            placeholderFetchpriority="high"
            loadStrategy="instant"
            width="100%"
            objectFit="cover"
            preload
          />
        )}
      imageCaption={imageCaption}
      socialButtons={<SocialShare data={filteredShareList} />}
      labels={labels}
      sponsorMessage={
        isSponsored || reference === 'sponsor'
          ? intl.formatMessage(messages.partnerContent).toUpperCase() : null
      }
      pressReleaseMessage={
        (category && category.path.includes('press')) || reference === 'press'
          ? intl.formatMessage(messages.pressReleaseMessage) : null
      }
    >
      <div>
        {tags.map(({ title: tagTitle, slug }) => (
          <Link
            route="tag"
            params={{ slug }}
            passHref
            key={`article-${tagTitle}`}
          >
            <Anchor styled>
              <TagChip
                label={tagTitle}
              />
            </Anchor>
          </Link>
        ))}
      </div>
      <DynamicShareButtonContainer center>
        <SocialShare data={filteredShareList} />
      </DynamicShareButtonContainer>
      <DynamicNewsletterComponent showDescriptiveText={false} />
    </ArticleComponent>
  );
};

Main.propTypes = {
  article: PropTypes.shape({
    category: PropTypes.shape({
      title: PropTypes.string,
      path: PropTypes.string,
    }),
    title: PropTypes.string,
    creator: PropTypes.shape({}),
    createdAt: PropTypes.string,
    imageUrls: PropTypes.arrayOf(PropTypes.string),
    imageCaption: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
    })),
    body: PropTypes.string,
    reference: PropTypes.string,
    labels: PropTypes.arrayOf(PropTypes.string),
  }),
  ArticleComponent: PropTypes.elementType,
  srcSet: PropTypes.string.isRequired,
};

Main.defaultProps = {
  article: {},
  ArticleComponent: ArticleDetail,
};

export default Main;
